import React, { useCallback } from 'react';

import {
  useFlowStore,
  useUiStore,
  useFlowRegistry,
  useSessionStore,
  useFlowStoreInstance,
} from '../store/react';
import {
  selectCurrentItem,
  selectCurrentLoop,
  selectLoopsDefinitions,
} from '../store/selectors';
import { getShortLoopName } from '../utils.js';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { FORM_CLASS, Item } from './item.jsx';
import { WarningMessage } from '../utils_components.jsx';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

import DawidDisclaimer from './disclaimer.jsx';
import ItemErrorHandler from './item_error_handler.jsx';
import Footer from './footer.jsx';
import HeaderWrapper from './wrappers/HeaderWrapper.jsx';
import MenuWrapper from './wrappers/MenuWrapper.jsx';
import FormPathWrapper from './wrappers/FormPathWrapper.jsx';
import ButtonsBarWrapper from './wrappers/ButtonsBarWrapper.jsx';
import LoopsNavigationWrapper from './wrappers/LoopsNavigationWrapper.jsx';
import PreliminarySectionWrapper from './wrappers/PreliminarySectionWrapper.jsx';
import ItemWrapper from './wrappers/ItemWrapper.jsx';
import ContinuousFlowWrapper from './wrappers/ContinuousFlowWrapper.jsx';
import ContractWrapper from './wrappers/ContractWrapper.jsx';
import { FlowComplete } from './flow_complete.jsx';

export default function ShellBody() {
  const registry = useFlowRegistry();
  const showSummary = useUiStore((s) => s.showSummary);
  const continuousMode = useUiStore((s) => s.continuousMode);
  const hasGlobalError = useUiStore((s) => s.hasGlobalError);
  const globalErrorInfo = useUiStore((s) => s.globalErrorInfo);
  const setGlobalError = useUiStore((s) => s.setGlobalError);
  const setMenuFixed = useUiStore((s) => s.setMenuFixed);

  const onVisible = useCallback(() => setMenuFixed(false), [setMenuFixed]);
  const onHidden = useCallback(() => setMenuFixed(true), [setMenuFixed]);
  const sentinelRef = useIntersectionObserver({ onVisible, onHidden });

  const currentFlowId = useFlowStore((s) => s.currentFlowId);
  const currentItem = useFlowStore((s) => selectCurrentItem(s, registry));
  const currentLoop = useFlowStore((s) => selectCurrentLoop(s, registry));
  const loopsDefinitions = useFlowStore((s) => selectLoopsDefinitions(s, registry));

  const flow = registry.get(currentFlowId);
  const loopName = currentItem && currentItem.loopName;

  let isLoopPreliminaryAllowed = false;
  let sectionName;
  let loopType;
  if (loopName && currentLoop) {
    const shortLoopName = getShortLoopName(loopName);
    const friendlyLoop = loopsDefinitions[shortLoopName];
    loopType = friendlyLoop && friendlyLoop.loopType;
    isLoopPreliminaryAllowed = !!(friendlyLoop && friendlyLoop.allowPreliminary);
    sectionName = friendlyLoop && friendlyLoop.namePreliminarySection;
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <div id="content" className="flex-1">
        {flow && flow.disclaimerText && <DawidDisclaimer disclaimer={flow.disclaimerText} />}
        <HeaderWrapper />

        <div className="max-w-5xl mx-auto px-4 pb-12">
          <ButtonsBarWrapper />

          {/* Sentinel: when this scrolls out of view, menu becomes sticky */}
          <div ref={sentinelRef} className="no-print h-px" />
          <MenuWrapper />

          <div className="flex gap-6 min-h-[40vh] mt-4">
            {!showSummary && (
              <aside className="w-56 shrink-0">
                <FormPathWrapper />
              </aside>
            )}

            <main id="mainContent" className="flex-1 min-w-0 space-y-4">
              {/* Continuous mode renders every iteration inline with its own loop
                  navigator, so the single-item loop pagination is redundant there. */}
              {!showSummary && !continuousMode && currentLoop && <LoopsNavigationWrapper />}
              {!showSummary && !continuousMode && currentLoop && isLoopPreliminaryAllowed && (
                <PreliminarySectionWrapper sectionName={sectionName} />
              )}
              <ItemErrorHandler onError={setGlobalError}>
                {!hasGlobalError && !showSummary && currentItem && currentItem.id !== 'theEnd' && (
                  continuousMode ? <ContinuousFlowWrapper /> : <ItemWrapper loopType={loopType} />
                )}
                {!hasGlobalError && showSummary && <SummaryList />}
                {!hasGlobalError && currentItem && currentItem.id === 'theEnd' && (
                  flow?.outputLabel ? <ContractWrapper /> : <FlowComplete />
                )}
              </ItemErrorHandler>
              {hasGlobalError && (
                <div className={cn(FORM_CLASS, "p-5")}>
                  <Badge
                    variant="accent"
                    className="absolute top-0 left-0 right-0 rounded-t-2xl rounded-b-none text-center justify-center py-1"
                  >
                    An unforeseen error occurred
                  </Badge>
                  <div className="pt-8">
                    <WarningMessage
                      key="errorGlobal"
                      header="An unforeseen error occurred"
                      message={globalErrorInfo}
                    />
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function SummaryList() {
  const flowStore = useFlowStoreInstance();
  useFlowStore((s) => s.itemsStates);
  useFlowStore((s) => s.loops);
  useFlowStore((s) => s.globals);
  useFlowStore((s) => s.path);
  const mode = useSessionStore((s) => s.mode);
  const walk = flowStore.engine.traverse();
  return (
    <div className="flex flex-col gap-3">
      {walk.map((entry, index) => (
        <Item
          key={'summary-item-' + index}
          item={entry.item}
          loopIndex={entry.loopIndex}
          isObserver={mode === 'observer'}
          values={entry.values}
          preliminaries={entry.preliminaries}
          hasErrors={false}
          readOnly
        />
      ))}
    </div>
  );
}
